import { NextRouter } from "next/router";
import {
  BillOfMaterials,
  Board,
  BOMState,
  PhysicalDetailsState,
  QuoteState,
  setView,
} from "../../reducers/uploadedBoard";
import { AppDispatch } from "../store";
import { MouseEvent } from "react";
import { ORDER_COMPLETE, UPLOAD_BOARD } from "../../constants/routes";

import { DEMO_BOARD_SLUG } from "../../constants/boards";
import { requestingNewTab } from "../hooks/links";

export enum BoardView {
  BOARD = "Board",
  BOM = "BOM",
  QUOTE = "Quote",
  MANUFACTURING = "Manufacturing",
}

export interface StepContext {
  router: NextRouter;
  dispatch: AppDispatch;
  board: Board | null;
  billOfMaterials: BillOfMaterials | null;
  view: BoardView | null;
  isAdminOrEngineer: boolean;
}

interface Step {
  label: string;
  view: BoardView | null;
  shouldDisplay: (context: StepContext) => boolean;
  isSelected: (context: StepContext) => boolean;
  isProcessing: (context: StepContext) => boolean;
  isCompleted: (context: StepContext) => boolean;
  onClick: (event: MouseEvent<Element>, context: StepContext) => void;
}

function alwaysOn() {
  return true;
}

const UPLOAD_BOARD_STEP: Step = {
  label: "Upload Files",
  view: null,
  shouldDisplay: alwaysOn,
  isSelected: (context: StepContext) => {
    return context.router.pathname == UPLOAD_BOARD;
  },
  isProcessing: () => {
    return false;
  },
  isCompleted: (context: StepContext) => {
    return context.router.pathname != UPLOAD_BOARD;
  },
  onClick: (event: MouseEvent<Element>, context: StepContext) => {
    event.preventDefault();
    if (requestingNewTab(event)) {
      window.open(UPLOAD_BOARD, "_blank");
    } else {
      void context.router.push(UPLOAD_BOARD);
    }
  },
};

function isOrderReceivedPage(context: StepContext) {
  return context.router.pathname == ORDER_COMPLETE;
}

export function getBoardViewPath(boardId: string, view: BoardView): string {
  return `/review-board/${boardId}?view=${view}`;
}

function setBoardView(
  event: MouseEvent,
  view: BoardView,
  context: StepContext,
) {
  event.preventDefault();
  // From the upload board page or order complete page do nothing
  if (context.board === null) {
    return;
  }
  if (requestingNewTab(event)) {
    window.open(getBoardViewPath(context.board.id, view), "_blank");
  } else {
    context.dispatch(setView({ boardId: context.board.id, view }));
  }
}

function isView(view: BoardView) {
  return (context: StepContext) => context.view == view;
}

function physicalDetailsComplete(context: StepContext) {
  if (context.board === null) {
    return false;
  } else {
    return (
      context.board.physical_details_state == PhysicalDetailsState.PROCESSED
    );
  }
}

function bomComplete(context: StepContext) {
  if (context.billOfMaterials === null) {
    return false;
  } else {
    return context.billOfMaterials.state == BOMState.PROCESSED;
  }
}

function quoteComplete(context: StepContext) {
  if (context.board === null) {
    return false;
  } else {
    return context.board.quote.state == QuoteState.QUOTED;
  }
}

function boardIsOrdered(context: StepContext) {
  if (context.board === null) {
    return false;
  } else {
    return context.board.quote.state == QuoteState.ORDER_RECEIVED;
  }
}

const REVIEW_BOARD_STEP: Step = {
  label: "Review Board",
  view: BoardView.BOARD,
  shouldDisplay: alwaysOn,
  isSelected: isView(BoardView.BOARD),
  isProcessing: (context: StepContext) => {
    return (
      context.board?.physical_details_state == PhysicalDetailsState.PROCESSING
    );
  },
  isCompleted: (context: StepContext) => {
    return isOrderReceivedPage(context) || physicalDetailsComplete(context);
  },
  onClick: (event, context: StepContext) => {
    return setBoardView(event, BoardView.BOARD, context);
  },
};

const BOM_STEP: Step = {
  label: "Bill of Materials",
  view: BoardView.BOM,
  shouldDisplay: alwaysOn,
  isSelected: isView(BoardView.BOM),
  isProcessing: (context: StepContext) => {
    return context.billOfMaterials?.state == BOMState.PROCESSING;
  },
  isCompleted: (context: StepContext) => {
    return isOrderReceivedPage(context) || bomComplete(context);
  },
  onClick: (event: MouseEvent, context: StepContext) => {
    return setBoardView(event, BoardView.BOM, context);
  },
};

const QUOTE_STEP: Step = {
  label: "Quote",
  view: BoardView.QUOTE,
  shouldDisplay: alwaysOn,
  isSelected: isView(BoardView.QUOTE),
  isProcessing: (context: StepContext) => {
    return context.board?.quote.state == QuoteState.UPDATING_QUOTE;
  },
  isCompleted: (context: StepContext) =>
    isOrderReceivedPage(context) || quoteComplete(context),
  onClick: (event: MouseEvent, context: StepContext) =>
    setBoardView(event, BoardView.QUOTE, context),
};

const MANUFACTURING_STEP: Step = {
  label: "Manufacturing",
  view: BoardView.MANUFACTURING,
  shouldDisplay: (context: StepContext) =>
    context.isAdminOrEngineer && context.board?.id !== DEMO_BOARD_SLUG,
  isSelected: isView(BoardView.MANUFACTURING),
  isProcessing: () => false,
  isCompleted: (context: StepContext) =>
    isOrderReceivedPage(context) || boardIsOrdered(context),
  onClick: (event: MouseEvent, context: StepContext) =>
    setBoardView(event, BoardView.MANUFACTURING, context),
};

export const ORDERING_STEPS: Step[] = [
  UPLOAD_BOARD_STEP,
  REVIEW_BOARD_STEP,
  BOM_STEP,
  QUOTE_STEP,
  MANUFACTURING_STEP,
];
